import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { formatRange, parseDate, typeColor, relativeLabel } from '../lib/format.js';
import { VERDICT } from '../components/Verdict.jsx';
import { EmpfehlungsBadge } from '../components/Empfehlung.jsx';
import PvpBadge from '../components/PvpBadge.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import Timeline from '../components/Timeline.jsx';

export const RAID_TIER = {
  super_mega: { label: 'Super-Mega-Raid', kurz: 'Super-Mega', farbe: '#d94f6a' },
  mega:       { label: 'Mega-Raid',       kurz: 'Mega',       farbe: '#8b7bff' },
  '5':        { label: 'Stufe 5',         kurz: 'Stufe 5',    farbe: '#d99026' },
  shadow:     { label: 'Crypto-Raid',     kurz: 'Crypto',     farbe: '#6b6472' },
  dyna:       { label: 'Dyna-Kampf',      kurz: 'Dyna',       farbe: '#e0533d' },
  '3':        { label: 'Stufe 3',         kurz: 'Stufe 3',    farbe: '#4d9bff' },
  '1':        { label: 'Stufe 1',         kurz: 'Stufe 1',    farbe: '#7b8496' },
};

// Reihenfolge der Abschnitte: was jetzt geht, steht oben.
const ABSCHNITTE = [
  { key: 'live', titel: 'Läuft gerade', text: 'Diese Bosse kannst du jetzt angehen.' },
  { key: 'rotation', titel: 'Dauerrotation', text: 'Läuft bis auf Weiteres, ohne angekündigtes Ende.' },
  { key: 'upcoming', titel: 'Kommt noch', text: 'Zeitfenster, die noch nicht offen sind.' },
  { key: 'past', titel: 'Vorbei', text: 'Abgelaufene Fenster — als Referenz, wann etwas zuletzt lief.' },
];

export default function RaidsPage() {
  const { user } = useAuth();
  const [bosse, setBosse] = useState(null);
  const [error, setError] = useState(null);
  const [nurLohnend, setNurLohnend] = useState(false);
  const [stufe, setStufe] = useState('all');

  useEffect(() => {
    let aktiv = true;
    api.raids.list()
      .then((d) => aktiv && setBosse(d.bosses))
      .catch((e) => aktiv && setError(e.message));
    return () => { aktiv = false; };
  }, [user]);

  const gefiltert = useMemo(() => {
    if (!bosse) return [];
    return bosse.filter((b) => {
      if (stufe !== 'all' && b.tier !== stufe) return false;
      if (nurLohnend) return b.worth && (b.worth.verdict === 'strong' || b.worth.verdict === 'solid');
      return true;
    });
  }, [bosse, nurLohnend, stufe]);

  // Die Zeitachse erwartet dieselbe Form wie bei den Events: etwas mit
  // Anfang, Ende und Titel. Abgelaufene Fenster bleiben draussen — sie
  // wuerden die Achse nur in die Vergangenheit strecken und stauchen.
  const achsenDaten = useMemo(() => gefiltert
    .filter((b) => b.status !== 'past' && !b.openEnded)
    .map((b) => ({
      id: b.id,
      slug: b.pokemon.slug,
      title: `${b.pokemon.name}${b.pokemon.form ? ' ' + b.pokemon.form : ''}`,
      eventType: 'other',
      accentColor: RAID_TIER[b.tier]?.farbe ?? null,
      startsAt: b.startsAt,
      endsAt: b.endsAt,
      worthTier: b.worth?.tier ?? null,
      pvp: b.pvp ?? null,
    })), [gefiltert]);

  const liveAnzahl = (bosse ?? []).filter((b) => b.status === 'live').length;

  return (
    <>
      <section className="hero">
        <span className="hero__tag">
          {liveAnzahl > 0 ? `${liveAnzahl} Boss${liveAnzahl === 1 ? '' : 'e'} gerade aktiv` : 'Raid-Bosse'}
        </span>
        <h1>Wer steckt gerade in den Arenen?</h1>
        <p className="hero__sub">
          Aktive und kommende Raid-Fenster — jeweils mit der Einschätzung, ob der
          Kampf den Pass wert ist.
        </p>
      </section>

      <div className="filters">
        <button
          type="button"
          className={`chip${stufe === 'all' ? ' chip--on' : ''}`}
          onClick={() => setStufe('all')}
        >
          Alle Stufen
        </button>
        {Object.entries(RAID_TIER).map(([key, t]) => {
          // Nur Stufen anbieten, zu denen es auch Bosse gibt.
          if (!(bosse ?? []).some((b) => b.tier === key)) return null;
          return (
            <button
              key={key}
              type="button"
              className={`chip${stufe === key ? ' chip--on' : ''}`}
              onClick={() => setStufe(key)}
            >
              {t.kurz}
            </button>
          );
        })}
        <button
          type="button"
          className={`chip${nurLohnend ? ' chip--on' : ''}`}
          onClick={() => setNurLohnend((n) => !n)}
        >
          Nur lohnende
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {!bosse && !error && <div className="skeleton" style={{ height: 200 }} />}

      {gefiltert.length > 0 && (
        <Timeline
          events={achsenDaten}
          titel="Raid-Fenster"
          linkFor={(e) => `/pokemon/${e.slug}`}
          marken={balkenMarken}
        />
      )}

      {bosse && gefiltert.length === 0 && (
        <div className="empty">
          <div className="empty__icon">✧</div>
          <p><strong>Keine Raid-Bosse erfasst.</strong></p>
          <p>Sobald Raid-Fenster hinterlegt sind, stehen sie hier.</p>
        </div>
      )}

      {ABSCHNITTE.map(({ key, titel, text }) => {
        const gruppe = key === 'rotation'
          ? gefiltert.filter((b) => b.openEnded)
          : gefiltert.filter((b) => b.status === key && !b.openEnded);
        if (gruppe.length === 0) return null;

        return (
          <section className="gruppe" key={key}>
            <div className="gruppe__kopf">
              <h2 className="gruppe__titel">{titel}</h2>
              <span className="gruppe__anzahl">{gruppe.length}</span>
            </div>
            <p className="gruppe__text">{text}</p>
            <div className="grid">
              {gruppe.map((b) => <BossKarte key={b.id} boss={b} />)}
            </div>
          </section>
        );
      })}
    </>
  );
}

/**
 * Kurzzeichen am Balken: R für einen lohnenden Raid, P für den Trainerkampf.
 *
 * Nur bei den guten Stufen — wäre jeder Balken markiert, sagt die Marke
 * nichts mehr aus. Beim Trainerkampf zählt B mit, weil dort schon eine solide
 * Einstufung reicht, um ein Pokémon mitzunehmen.
 */
function balkenMarken(e) {
  const liste = [];

  if (e.worthTier === 'S' || e.worthTier === 'A') {
    liste.push({
      kurz: 'R',
      titel: 'Lohnender Raid — Stufe ' + e.worthTier,
      farbe: 'var(--verdict-strong)',
    });
  }

  const pvpStufe = e.pvp?.tier;
  if (pvpStufe === 'S' || pvpStufe === 'A' || pvpStufe === 'B') {
    liste.push({
      kurz: 'P',
      titel: 'Trainerkampf — Stufe ' + pvpStufe
        + (e.pvp.league ? ' (' + (RAID_LIGA_NAME[e.pvp.league] ?? e.pvp.league) + ')' : ''),
      farbe: 'var(--accent-3)',
    });
  }

  return liste;
}

const RAID_LIGA_NAME = { great: 'Superliga', ultra: 'Hyperliga', master: 'Meisterliga' };

function BossKarte({ boss }) {
  const stufe = RAID_TIER[boss.tier] ?? { label: boss.tier, farbe: 'var(--accent)' };
  const urteil = boss.worth ? VERDICT[boss.worth.verdict] : null;
  const p = boss.pokemon;

  return (
    <Link
      to={`/pokemon/${p.slug}`}
      className={`card mon-card${boss.status === 'past' ? ' mon-card--vorbei' : ''}`}
      style={{ '--card-accent': stufe.farbe }}
    >
      <div className="card__top">
        <span className="badge" style={{ '--badge-color': stufe.farbe }}>{stufe.label}</span>
        {boss.openEnded
          ? <span className="badge badge--soft">Dauerhaft</span>
          : boss.status === 'live'
            ? <span className="badge badge--live">Aktiv</span>
            : <span className="badge badge--soft">{relativeLabel(boss)}</span>}
      </div>

      <div>
        <div className="card__title">
          {p.name}
          {p.form && <span className="mon-card__form"> {p.form}</span>}
        </div>
        <div className="card__meta">
          {boss.openEnded
            ? 'Läuft bis auf Weiteres'
            : formatRange(boss.startsAt, boss.endsAt)}
          {boss.endsEstimated && !boss.openEnded && (
            <span className="card__meta-hinweis" title="Das Enddatum ist nicht belegt und geschätzt."> · Ende geschätzt</span>
          )}
        </div>
      </div>

      <div className="inline">
        {p.types.map((t) => (
          <span className="type" key={t} style={{ '--type-color': typeColor(t) }}>{t}</span>
        ))}
        {boss.shinyAvailable && <span className="badge badge--soft">Shiny möglich</span>}
        <PvpBadge pvp={boss.pvp} />
        {boss.region && <span className="badge badge--soft" title="Nur in dieser Region verfügbar">📍 {boss.region}</span>}
      </div>

      {urteil && (
        <div className="mon-card__event">
          <div className="inline" style={{ marginBottom: 6 }}>
            <span className="badge" style={{ '--badge-color': urteil.farbe }}>{urteil.label}</span>
            <span className={`tier tier--${boss.worth.tier}`}>{boss.worth.tier}</span>
          </div>
          {boss.worth.notes && <p className="card__summary">{boss.worth.notes}</p>}
        </div>
      )}

      {boss.personal && (
        <div className="inline"><EmpfehlungsBadge personal={boss.personal} /></div>
      )}

      {boss.note && <p className="mon-card__weitere">{boss.note}</p>}
    </Link>
  );
}
